import '../css/BillingPage.less';

import React, {
  FunctionComponent,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {Button, Modal, Popup} from 'semantic-ui-react';
import {Stripe} from '@stripe/stripe-js';
import {Elements} from '@stripe/react-stripe-js';
import {Link} from 'react-router-dom';
import numeral from 'numeral';

import LegacyWBIcon from '../components/elements/LegacyWBIcon';
import {EditSubscriptionForm} from '../components/EditSubscriptionForm';
import {SubscribeForm} from '../components/SubscribeForm';
import {StorageSubscribeForm} from '../components/StorageSubscribeForm';
import {CreateTeamModal} from '../components/CreateTeamModal';
import {UpdatePaymentForm} from '../components/UpdatePaymentForm';
import {useViewer} from '../state/viewer/hooks';
import WandbLoader from '../components/WandbLoader';
import {
  useSubscriptionPlansQuery,
  Plan,
  useCustomerSubscriptionInfoQuery,
  CustomerSubscriptionInfoQuery,
  useEntityStorageQuery,
  OrganizationSubscriptionType,
  OrgType,
  PlanType,
} from '../generated/graphql';
import {propagateErrorsContext} from '../util/errors';
import _ from 'lodash';
import EditUserModal from '../components/EditUserModal';
import Config from '../config';
import {profilePage, storagePage, teamPage} from '../util/urls';
import makeComp from '../util/profiler';
import docUrl from '../util/doc_urls';
import * as S from './BillingPage.styles';

import StoragePercentageBilling from '../components/StoragePercentageBilling';
import config from '../config';
import {useScrollToURLHash} from '../util/document';
import {bytesInGB, kilobytes} from '../util/storage';

const startupTitle = 'Startup';
const startupSubtitle =
  'Private collaboration on a small scale for pre-funded startups';
const startupFeatures = [
  'Up to 3 users',
  '1 team',
  'Unlimited public & private projects',
  'Email support',
  'Hyperparameter optimization tools',
];

const teamsTitle = 'Teams';
const teamsSubtitle = 'Essential management and security for small teams';
const teamsFeatures = [
  'Up to 5 users',
  '1 team',
  'Unlimited public & private projects',
  'Email support',
  'Hyperparameter optimization tools',
  'Service accounts',
];

const storageTitle = 'Usage Plan';
const storageSubtitle =
  'Unlock more storage and artifacts with a subscription plan. Free accounts include 100 GB of storage and artifact tracking. Subscribe now, and only pay for usage beyond the free plan.';
const storageActiveSubtitle =
  'Your subscription lets you only pay for the storage and artifacts that you use each month';

const storageFeatureTitle = 'Storage';
const artifactFeatureTitle = 'Artifact tracking';
const bytesInTB = bytesInGB * kilobytes;
// TODO: Grab the prices & tiers info from Stripe
const storagePrices = [0.0, 0.08, 0.06, 0.05];
const artifactPrices = [0.0, 0.05, 0.03, 0.02];
const storageTiers = [
  bytesInGB * 100,
  bytesInTB * 10,
  bytesInTB * 100,
  bytesInTB * 1000,
];
const storageFeatures = [
  `$${storagePrices[1]} per GB up to ${storageTiers[1] / bytesInTB} TB`,
  `$${storagePrices[2]} per GB up to ${storageTiers[2] / bytesInTB} TB`,
  `$${storagePrices[3]} per GB up to ${storageTiers[3] / bytesInTB} TB`,
  `Over ${storageTiers[3] / bytesInTB} TB, contact us`,
];
const artifactFeatures = [
  `$${artifactPrices[1]} per GB up to ${storageTiers[1] / bytesInTB} TB`,
  `$${artifactPrices[2]} per GB up to ${storageTiers[2] / bytesInTB} TB`,
  `$${artifactPrices[3]} per GB up to ${storageTiers[3] / bytesInTB} TB`,
  `Over ${storageTiers[3] / bytesInTB} TB, contact us`,
];
export interface PlanTable {
  storageFeatureTitle: string;
  artifactFeatureTitle: string;
  storageFeatures: string[];
  artifactFeatures: string[];
}

const pricingPlans: PlanTable = {
  storageFeatureTitle,
  artifactFeatureTitle,
  storageFeatures,
  artifactFeatures,
};

const planDisplayName = (planName: string) => {
  return _.capitalize(planName.replace('_', ' '));
};

type Maybe<T> = T | null | undefined;
interface PlanInfo {
  title: string;
  subtitle: string;
  features: string[];
  monthlyPlan: Maybe<Plan>;
  yearlyPlan: Maybe<Plan>;
}

interface ArtifactPlanInfo {
  title: string;
  subtitle: string;
  features: string[];
  storagePlan: Maybe<Plan>;
  trackingPlan: Maybe<Plan>;
}

export type OrganizationMemberFragment = NonNullable<
  CustomerSubscriptionInfoQuery['viewer']
>['organizations'][number]['members'][number];

export interface OrganizationFragment {
  id: string;
  name: string;
  subscriptionStatus: string;
  cancelAtPeriodEnd: boolean;
  members?: OrganizationMemberFragment[];
  billingUserName?: string | null | undefined;
}

export interface PlanFragment {
  type: string;
}

let globalStripePromise: Promise<Stripe | null> | null = null;

const BillingPage: FunctionComponent = makeComp(
  () => {
    useScrollToURLHash();
    // Load stripe before anything else.
    const [
      stripePromise,
      setStripePromise,
    ] = useState<Promise<Stripe | null> | null>(globalStripePromise);
    useEffect(() => {
      if (globalStripePromise != null) {
        return;
      }
      if (Config.ENVIRONMENT_IS_PRIVATE || Config.ENVIRONMENT_NAME === 'test') {
        return;
      }
      const stripeApiKey = Config.STRIPE_API_KEY;
      if (stripeApiKey == null) {
        throw new Error('Stripe API key not set!');
      }

      import('@stripe/stripe-js').then(module => {
        const stripeLoadResult = module.loadStripe(stripeApiKey);
        setStripePromise(stripeLoadResult);
        globalStripePromise = stripeLoadResult;
      });
    }, []);

    const viewer = useViewer();
    const plans = useSubscriptionPlansQuery({
      context: propagateErrorsContext(),
    });
    const subscriptionInfo = useCustomerSubscriptionInfoQuery({
      fetchPolicy: 'network-only',
    });

    useEffect(() => {
      if (viewer) {
        window.analytics.track('Billing page opened', {viewer});
      }
    }, [viewer]);

    const [storageCheckoutModalOpen, setStorageCheckoutModalOpen] = useState(
      false
    );
    const [storagePricingModalOpen, setStoragePricingModalOpen] = useState(
      false
    );
    const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
    const [updatePaymentModalOpen, setUpdatePaymentModalOpen] = useState(false);
    const [editUserModalOpen, setEditUserModalOpen] = useState(false);
    const [editSubscriptionModalOpen, setEditSubscriptionModalOpen] = useState(
      false
    );
    const [selectedPlanInfo, setSelectedPlanInfo] = useState<PlanInfo | null>(
      null
    );
    const [
      selectedOrganization,
      setSelectedOrganization,
    ] = useState<OrganizationFragment | null>(null);
    const [createTeamModalOpen, setCreateTeamModalOpen] = useState(false);

    const plansInfo = useMemo(() => {
      const startupMonthly = plans.data?.plans.find(
        p => p?.name === 'startup_monthly'
      );
      const startupYearly = plans.data?.plans.find(
        p => p?.name === 'startup_yearly'
      );

      const startupPlanInfo: PlanInfo = {
        title: startupTitle,
        subtitle: startupSubtitle,
        features: startupFeatures,
        monthlyPlan: startupMonthly,
        yearlyPlan: startupYearly,
      };

      const teamsMonthly = plans.data?.plans.find(
        p => p?.name === 'teams_monthly'
      );
      const teamsYearly = plans.data?.plans.find(
        p => p?.name === 'teams_yearly'
      );

      const teamsPlanInfo: PlanInfo = {
        title: teamsTitle,
        subtitle: teamsSubtitle,
        features: teamsFeatures,
        monthlyPlan: teamsMonthly,
        yearlyPlan: teamsYearly,
      };

      const storageMonthly = plans.data?.plans.find(
        p => p?.name === 'storage_monthly'
      );
      const trackingMonthly = plans.data?.plans.find(
        p => p?.name === 'tracking_monthly'
      );

      const basePlanInfo = () => ({
        title: storageTitle,
        subtitle: storageSubtitle,
        features: [],
      });
      const artifactPlanInfo: ArtifactPlanInfo = {
        ...basePlanInfo(),
        storagePlan: storageMonthly,
        trackingPlan: trackingMonthly,
      };
      const storagePlanInfo: PlanInfo = {
        ...basePlanInfo(),
        monthlyPlan: storageMonthly,
        yearlyPlan: storageMonthly,
      };
      const storageActivePlanInfo: PlanInfo = {
        ...basePlanInfo(),
        subtitle: storageActiveSubtitle,
        monthlyPlan: storageMonthly,
        yearlyPlan: storageMonthly,
      };

      return {
        startup: startupPlanInfo,
        teams: teamsPlanInfo,
        storage: storagePlanInfo,
        storageActive: storageActivePlanInfo,
        artifact: artifactPlanInfo,
      };
    }, [plans]);

    const handleStartupClicked = useCallback(() => {
      if (
        plansInfo.startup.monthlyPlan == null ||
        plansInfo.startup.yearlyPlan == null
      ) {
        return;
      }

      window.analytics.track('Subscribe to startup clicked', {viewer});

      setSelectedPlanInfo(plansInfo.startup);
      setCheckoutModalOpen(true);
    }, [setSelectedPlanInfo, plansInfo, setCheckoutModalOpen, viewer]);

    const handleStorageClicked = useCallback(() => {
      window.analytics.track('Subscribe to storage clicked', {viewer});

      setSelectedPlanInfo(plansInfo.storage);
      setStorageCheckoutModalOpen(true);
    }, [setSelectedPlanInfo, plansInfo, setStorageCheckoutModalOpen, viewer]);

    const handlePricingClicked = useCallback(() => {
      window.analytics.track('Storage pricing clicked', {viewer});

      setSelectedPlanInfo(plansInfo.storageActive);
      setStoragePricingModalOpen(true);
    }, [setSelectedPlanInfo, plansInfo, setStoragePricingModalOpen, viewer]);

    const userEntity = useEntityStorageQuery({
      fetchPolicy: 'network-only',
      variables: {
        entityName: viewer?.entity,
        enableReferenceTracking: false,
      },
    });

    const selectedOrgOption = useMemo(() => {
      if (selectedOrganization == null) {
        return [];
      }
      return [
        {
          text: selectedOrganization.name ?? '',
          value: selectedOrganization.id ?? '',
        },
      ];
    }, [selectedOrganization]);

    const handleTeamsClicked = useCallback(() => {
      if (
        plansInfo.teams.monthlyPlan == null ||
        plansInfo.teams.yearlyPlan == null
      ) {
        return;
      }

      window.analytics.track('Subscribe to teams clicked', {viewer});

      setSelectedPlanInfo(plansInfo.teams);
      setCheckoutModalOpen(true);
    }, [setSelectedPlanInfo, plansInfo, setCheckoutModalOpen, viewer]);

    if (!viewer || plans.loading || stripePromise == null) {
      return <WandbLoader />;
    }

    const orgMap = subscriptionInfo.data?.viewer?.organizations.filter(
      org => org.orgType === OrgType.Organization
    );
    const personalMap = subscriptionInfo.data?.viewer?.organizations.filter(
      org => org.orgType === OrgType.Personal
    );

    const renderOrgCard = (org: NonNullable<typeof orgMap>[number]) => {
      const isPersonal = org.orgType === OrgType.Personal;
      const billingInfo = org.stripeBillingInfo;
      const primarySub = org.subscriptions[0];
      const subIsActive =
        billingInfo?.status === 'active' ||
        primarySub.subscriptionType ===
          OrganizationSubscriptionType.Enterprise ||
        primarySub.subscriptionType === OrganizationSubscriptionType.Academic ||
        (isPersonal &&
          primarySub.subscriptionType === OrganizationSubscriptionType.Stripe);
      const subIsTrialing =
        billingInfo?.status === 'trialing' ||
        primarySub.subscriptionType ===
          OrganizationSubscriptionType.ManualTrial ||
        primarySub.subscriptionType ===
          OrganizationSubscriptionType.UserLedTrial ||
        primarySub.subscriptionType ===
          OrganizationSubscriptionType.AcademicTrial;
      const subIsCanceled =
        billingInfo?.status === 'canceled' ||
        billingInfo?.status === 'incomplete_expired';
      const subWillCancel = billingInfo?.cancelAtPeriodEnd;
      const subNeedsPayment = _.includes(
        ['incomplete', 'past_due', 'unpaid'],
        billingInfo?.status
      );
      const rawBillingDate = billingInfo?.currentPeriodEnd;
      const nextBillingDate = rawBillingDate
        ? new Date(rawBillingDate + 'Z').toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
        : null;
      const hasStorageSub = org.subscriptions.some(
        sub => sub.plan.planType === PlanType.Storage
      );
      const hasReferenceSub = org.subscriptions.some(
        sub => sub.plan.planType === PlanType.Reference
      );

      // Creates a comma-separated list of links to the members' profile pages
      const membersAndInvitees = [
        ..._.dropRight(
          _.flatten(
            org.members.map(m => [
              <a
                key={`profile-${org.name}-${m.username}`}
                href={profilePage(m.username)}>
                {m.username}
              </a>,
              ', ',
            ])
          )
        ),
        org.pendingInvites.map(pi => `, ${pi.email} (invited)`),
      ];

      const teams = [
        ..._.dropRight(
          _.flatten(
            org.teams.map(m => [
              <a key={`team-${org.name}-${m.name}`} href={teamPage(m.name)}>
                {m.name}
              </a>,
              ', ',
            ])
          )
        ),
        org.pendingInvites.map(pi => `, ${pi.email} (invited)`),
      ];

      const stripePaymentMethod = billingInfo?.paymentMethod;
      const paymentMethod =
        stripePaymentMethod?.cardType && stripePaymentMethod?.endingIn
          ? `${_.upperCase(stripePaymentMethod?.cardType)} •••• ${
              stripePaymentMethod.endingIn
            }`
          : `${
              stripePaymentMethod ? stripePaymentMethod.type + '--' : ''
            } payment details not available`;
      const isOrgAdminOrBillingOnlyUser =
        org.billingUser.username === viewer.username ||
        (org.members.find(m => m.username === viewer.username)?.admin ?? false);

      return (
        <>
          <S.SubscriptionOrgName data-test="org-name">
            {isPersonal && viewer.username}
            {!isPersonal && org.name}
          </S.SubscriptionOrgName>
          <S.SubscriptionCard>
            <S.SubscriptionCardRow>
              <S.SubscriptionCardPlan>
                <S.SubscriptionCardPlanHeader>
                  Account Plan{' '}
                  {!isPersonal && isOrgAdminOrBillingOnlyUser && (
                    <>
                      <Popup
                        position={'top center'}
                        trigger={
                          <span>
                            <LegacyWBIcon
                              data-test="edit-subscription"
                              link
                              style={{marginRight: 4}}
                              name="edit"
                              onClick={() => {
                                setSelectedOrganization({
                                  id: org.id,
                                  name: org.name,
                                  subscriptionStatus: billingInfo?.status ?? '',
                                  cancelAtPeriodEnd:
                                    billingInfo?.cancelAtPeriodEnd ?? false,
                                });
                                setEditSubscriptionModalOpen(true);
                              }}
                            />
                          </span>
                        }
                        content={'Edit/Cancel plan'}
                        size="small"
                      />
                      <span>
                        <LegacyWBIcon
                          link
                          style={{marginRight: 4}}
                          name="credit-card"
                          onClick={() => {
                            setSelectedOrganization({
                              id: org.id,
                              name: org.name,
                              subscriptionStatus: billingInfo?.status ?? '',
                              cancelAtPeriodEnd:
                                billingInfo?.cancelAtPeriodEnd ?? false,
                            });
                            setUpdatePaymentModalOpen(true);
                          }}
                        />{' '}
                      </span>
                    </>
                  )}
                </S.SubscriptionCardPlanHeader>
                {isPersonal ? (
                  <>
                    <S.AccountPlanContent>
                      <S.SubscriptionCardPlanType>
                        Personal account
                      </S.SubscriptionCardPlanType>
                      <S.SubscriptionCardLink
                        onClick={() => {
                          setSelectedOrganization(null);
                          setCreateTeamModalOpen(true);
                        }}>{`Create a team to collaborate →`}</S.SubscriptionCardLink>
                    </S.AccountPlanContent>
                  </>
                ) : (
                  <>
                    <S.SubscriptionCardPlanType>
                      <S.UsagePlanContentItemTitle>
                        Teams
                      </S.UsagePlanContentItemTitle>
                      {teams}
                    </S.SubscriptionCardPlanType>
                    <S.SubscriptionCardPlanType data-test="plan-info">
                      <S.UsagePlanContentItemTitle>
                        Billing
                      </S.UsagePlanContentItemTitle>
                      {`${planDisplayName(primarySub.plan.name)}, ${
                        primarySub.seats
                      } seats`}
                      {primarySub.plan.unitPrice &&
                        `; $${(primarySub.plan.unitPrice / 100) *
                          primarySub.seats} / ${
                          primarySub.plan.billingInterval
                        }`}
                    </S.SubscriptionCardPlanType>
                    <S.SubscriptionCardPlanType data-test="plan-status">
                      <S.UsagePlanContentItemTitle>
                        Status
                      </S.UsagePlanContentItemTitle>
                      {subIsActive && 'Active'}
                      {subIsTrialing && `In trial`}
                      {subIsCanceled && 'Canceled'}
                      {nextBillingDate &&
                        !subIsCanceled &&
                        !subWillCancel &&
                        ` (next bill ${nextBillingDate})`}
                      {subWillCancel &&
                        ` (subscription ends ${nextBillingDate ??
                          'at the end of the current cycle'})`}
                      {subNeedsPayment &&
                        'We had trouble receiving your payment.'}
                      {subNeedsPayment && org.stripeBillingInfo?.invoiceLink && (
                        <>
                          {' Click '}
                          <a href={org.stripeBillingInfo!.invoiceLink}>here</a>
                          {' to complete your payment manually.'}
                        </>
                      )}
                      , {isOrgAdminOrBillingOnlyUser && paymentMethod}
                      {!isOrgAdminOrBillingOnlyUser && 'Managed by your admin'}
                    </S.SubscriptionCardPlanType>
                    <S.SubscriptionCardPlanMembers data-test="members">
                      <S.UsagePlanContentItemTitle>
                        Members
                      </S.UsagePlanContentItemTitle>
                      {membersAndInvitees}
                      <S.AddMembers
                        data-test="edit-user-button"
                        onClick={() => {
                          setSelectedOrganization({
                            id: org.id,
                            name: org.name,
                            subscriptionStatus: billingInfo?.status ?? '',
                            cancelAtPeriodEnd:
                              billingInfo?.cancelAtPeriodEnd ?? false,
                            members: org.members,
                            billingUserName: org.billingUser.username,
                          });
                          setEditUserModalOpen(true);
                        }}>
                        Add members →
                      </S.AddMembers>
                    </S.SubscriptionCardPlanMembers>
                  </>
                )}
              </S.SubscriptionCardPlan>
              <S.SubscriptionCardPlan>
                <S.SubscriptionCardPlanHeader>
                  Usage Plan{' '}
                  {isPersonal && (
                    <>
                      <Popup
                        position={'top center'}
                        trigger={
                          <span>
                            <LegacyWBIcon
                              data-test="change-plan-button"
                              link
                              style={{marginRight: 4}}
                              name="edit"
                              onClick={handlePricingClicked}
                            />
                          </span>
                        }
                        content={'See pricing'}
                        size="small"
                      />
                      <Popup
                        position={'top center'}
                        trigger={
                          <>
                            <LegacyWBIcon
                              link
                              style={{marginRight: 4}}
                              name="credit-card"
                              onClick={() => {
                                setSelectedOrganization({
                                  id: org.id,
                                  name: org.name,
                                  subscriptionStatus: billingInfo?.status ?? '',
                                  cancelAtPeriodEnd:
                                    billingInfo?.cancelAtPeriodEnd ?? false,
                                });

                                setUpdatePaymentModalOpen(true);
                              }}
                            />
                          </>
                        }
                        content={'Change payment method'}
                        size="small"
                      />
                    </>
                  )}
                </S.SubscriptionCardPlanHeader>
                {isPersonal && (
                  <>
                    <S.UsagePlanContent>
                      <S.UsagePlanContentItem>
                        <S.UsagePlanContentItemTitle>
                          Storage
                        </S.UsagePlanContentItemTitle>
                        {numeral(userEntity.data?.entity?.storageBytes).format(
                          '0.0b'
                        )}
                      </S.UsagePlanContentItem>
                      <S.UsagePlanContentItem>
                        <S.UsagePlanContentItemTitle>
                          Artifacts
                        </S.UsagePlanContentItemTitle>
                        {numeral(
                          userEntity.data?.entity?.referenceBytes
                        ).format('0.0b')}
                      </S.UsagePlanContentItem>
                      <S.UsagePlanContentItem>
                        <S.UsagePlanContentItemTitle>
                          Status
                        </S.UsagePlanContentItemTitle>
                        {subIsActive && 'Active'}
                        {subIsTrialing && `In trial`}
                        {subIsCanceled && 'Canceled'}
                        {nextBillingDate &&
                          !subIsCanceled &&
                          !subWillCancel &&
                          ` (will bill again on ${nextBillingDate})`}
                        {subWillCancel &&
                          ` (subscription ends ${nextBillingDate ??
                            'at the end of the current cycle'})`}
                        {subNeedsPayment &&
                          'We had trouble receiving your payment.'}
                        {subNeedsPayment && org.stripeBillingInfo?.invoiceLink && (
                          <>
                            {' Click '}
                            <a href={org.stripeBillingInfo!.invoiceLink}>
                              here
                            </a>
                            {' to complete your payment manually.'}
                          </>
                        )}
                      </S.UsagePlanContentItem>
                    </S.UsagePlanContent>
                    <S.SubscriptionCardLink>
                      <Link
                        className="manage-storage"
                        to={storagePage(viewer.entity)}>
                        Manage →
                      </Link>
                    </S.SubscriptionCardLink>
                  </>
                )}
                {!isPersonal && (
                  <>
                    <S.UsagePlanTeams>
                      {org.teams.map(t => [
                        <>
                          <S.UsagePlanTeamInfo>
                            <S.UsagePlanTeamField>
                              <S.UsagePlanTeamFieldTitle>
                                {t.name}
                              </S.UsagePlanTeamFieldTitle>
                            </S.UsagePlanTeamField>
                            <S.UsagePlanTeamField>
                              <S.UsagePlanTeamFieldTitle>
                                Storage
                              </S.UsagePlanTeamFieldTitle>
                              {numeral(t.storage?.size ?? 0).format('0.0b')}
                            </S.UsagePlanTeamField>
                            <S.UsagePlanTeamField>
                              <S.UsagePlanTeamFieldTitle>
                                Artifacts
                              </S.UsagePlanTeamFieldTitle>
                              {numeral(t.artifact?.size ?? 0).format('0.0b')}
                            </S.UsagePlanTeamField>
                          </S.UsagePlanTeamInfo>
                        </>,
                      ])}
                      <S.UsagePlanDescription>
                        {!(hasStorageSub && hasReferenceSub) && (
                          <>
                            Upgrade your plan to include storage and artifacts
                            beyond the base 100 GB included in your plan.
                          </>
                        )}
                      </S.UsagePlanDescription>
                      <S.UsagePlanDescription>
                        {!(hasStorageSub && hasReferenceSub) &&
                          isOrgAdminOrBillingOnlyUser && (
                            <S.SeePricingButton>
                              <Button
                                data-test="upgrade-button"
                                className="action-button"
                                onClick={() => {
                                  setSelectedOrganization({
                                    id: org.id,
                                    name: org.name,
                                    subscriptionStatus:
                                      billingInfo?.status ?? '',
                                    cancelAtPeriodEnd:
                                      billingInfo?.cancelAtPeriodEnd ?? false,
                                  });
                                  handleStorageClicked();
                                }}>
                                Upgrade
                              </Button>
                            </S.SeePricingButton>
                          )}
                        <S.ContactSalesButton>
                          <Button
                            className="action-button"
                            onClick={() => {
                              window.analytics.track(
                                'Org storage plan contact us clicked',
                                {
                                  viewer,
                                }
                              );
                              window.open('http://wandb.com/demo');
                            }}>
                            Contact sales
                          </Button>
                        </S.ContactSalesButton>
                      </S.UsagePlanDescription>
                    </S.UsagePlanTeams>
                  </>
                )}
              </S.SubscriptionCardPlan>
            </S.SubscriptionCardRow>
          </S.SubscriptionCard>
        </>
      );
    };

    return (
      <Elements stripe={stripePromise}>
        <div className="billing-page">
          <div className="billing-page-container">
            <h1>Subscriptions</h1>

            {personalMap?.length === 0 && (
              <>
                <S.SubscriptionOrgName data-test="org-name">
                  {viewer.username}
                </S.SubscriptionOrgName>
                <S.SubscriptionCard>
                  <S.SubscriptionCardHeader>
                    Account Plan
                  </S.SubscriptionCardHeader>
                  <S.SubscriptionCardContentContainer>
                    <S.SubscriptionCardContentLeft>
                      Personal account, $0 / month
                    </S.SubscriptionCardContentLeft>
                    <S.SubscriptionCardContent>
                      <S.SubscriptionCardLink
                        onClick={() => {
                          setSelectedOrganization(null);
                          setCreateTeamModalOpen(true);
                        }}>{`Create a team to collaborate →`}</S.SubscriptionCardLink>
                    </S.SubscriptionCardContent>
                  </S.SubscriptionCardContentContainer>
                  <S.SubscriptionCardHeader>
                    Usage Plan
                  </S.SubscriptionCardHeader>
                  <S.SubscriptionCardContentContainer data-test="usage-desc">
                    <S.SubscriptionCardContentLeft>
                      You have 100 GB of free storage and artifacts. Subscribe
                      to unlock more space for your projects in W&B.
                    </S.SubscriptionCardContentLeft>
                    <S.SubscriptionCardContent>
                      <StoragePercentageBilling
                        storageAvailable={config.MAX_BYTES}
                        storageUsed={userEntity.data?.entity?.storageBytes}
                        artifactsAvailable={config.MAX_BYTES}
                        artifactsUsed={userEntity.data?.entity?.referenceBytes}
                      />
                    </S.SubscriptionCardContent>
                  </S.SubscriptionCardContentContainer>
                  <S.SubscriptionCardContentContainer>
                    <S.SubscriptionCardContentLeft>
                      <S.SeePricingButton>
                        <Button
                          data-test="see-pricing-button"
                          className="action-button"
                          onClick={handleStorageClicked}>
                          See Pricing
                        </Button>
                      </S.SeePricingButton>
                    </S.SubscriptionCardContentLeft>
                    <S.SubscriptionCardContentRight>
                      <Link
                        className="manage-storage"
                        to={storagePage(viewer.entity)}>
                        Manage usage →
                      </Link>
                    </S.SubscriptionCardContentRight>
                  </S.SubscriptionCardContentContainer>
                </S.SubscriptionCard>
              </>
            )}

            {personalMap?.map(renderOrgCard)}
            {orgMap?.map(renderOrgCard)}

            <UpdatePaymentModal
              open={updatePaymentModalOpen}
              organization={selectedOrganization}
              custoEmail={viewer.email}
              onTransactionCompleted={() => subscriptionInfo.refetch()}
              onClose={() => setUpdatePaymentModalOpen(false)}
            />
            <EditUserModal
              open={editUserModalOpen}
              organization={selectedOrganization}
              onAddUser={() => subscriptionInfo.refetch()}
              onRemoveUser={() => subscriptionInfo.refetch()}
              onUpdateOrganizationUser={() => subscriptionInfo.refetch()}
              onClose={() => setEditUserModalOpen(false)}
            />
            <EditSubscriptionModal
              open={editSubscriptionModalOpen}
              organization={selectedOrganization}
              onPlanEdited={() => subscriptionInfo.refetch()}
              onClose={() => setEditSubscriptionModalOpen(false)}
            />
            <CreateTeamModal
              open={createTeamModalOpen}
              orgOptions={selectedOrgOption}
              /* teamAdminUsername={
                selectedOrganization?.billingUser.username ?? undefined
              }*/

              onClose={() => setCreateTeamModalOpen(false)}
              onCreate={() => {}}
              // onCreate={() => organizations.refetch()}
            />

            <div className="pricing-panels" id="pricing-panels">
              <div className="pricing-panel">
                <div className="header">
                  <div className="panel-title">Personal</div>
                  <div className="panel-subtitle">
                    W&B basics for every practitioner
                  </div>
                </div>
                <div className="panel-content">
                  <div className="plan-feature">
                    Unlimited public & private projects
                  </div>
                  <div className="plan-feature">Public forum support</div>
                  <div className="plan-feature">
                    Hyperparameter optimization tools
                  </div>
                </div>
                <div className="footer">
                  <div className="plan-price">
                    <span className="price">Free</span>
                  </div>
                  <Button className="action-button current-plan">Active</Button>
                </div>
              </div>
              <div className="pricing-panel">
                <div className="header">
                  <div className="panel-title">{startupTitle}</div>
                  <div className="panel-subtitle">{startupSubtitle}</div>
                </div>
                <div className="panel-content">
                  {startupFeatures.map((sf, i) => (
                    <div key={`start-feature-${i}`} className="plan-feature">
                      {sf}
                    </div>
                  ))}
                </div>
                <div className="footer">
                  <div className="plan-price">
                    {plansInfo.startup.yearlyPlan?.unitPrice != null &&
                    plansInfo.startup.monthlyPlan?.unitPrice != null ? (
                      <>
                        <span className="price">{`$${plansInfo.startup
                          .yearlyPlan.unitPrice /
                          100 /
                          12}`}</span>
                        {` per user / month`}
                      </>
                    ) : (
                      <span className="price">Coming soon</span>
                    )}
                  </div>
                  <Button
                    className="action-button"
                    data-test="startup-button"
                    disabled={
                      plansInfo.startup.monthlyPlan == null ||
                      plansInfo.startup.yearlyPlan == null
                    }
                    onClick={handleStartupClicked}>
                    Start a trial
                  </Button>
                </div>
              </div>
              <div className="pricing-panel">
                <div className="header">
                  <div className="panel-title">{teamsTitle}</div>
                  <div className="panel-subtitle">{teamsSubtitle}</div>
                </div>
                <div className="panel-content">
                  {teamsFeatures.map((tf, i) => (
                    <div key={`team-feature-${i}`} className="plan-feature">
                      {tf}
                    </div>
                  ))}
                </div>
                <div className="footer">
                  <div className="plan-price">
                    {plansInfo.teams.yearlyPlan?.unitPrice != null &&
                    plansInfo.teams.monthlyPlan?.unitPrice != null ? (
                      <>
                        <span className="price">{`$${plansInfo.teams.monthlyPlan
                          .unitPrice / 100}`}</span>
                        {` per user / month`}
                      </>
                    ) : (
                      <span className="price">Coming soon</span>
                    )}
                  </div>
                  <Button
                    className="action-button"
                    data-test="teams-button"
                    disabled={
                      plansInfo.teams.monthlyPlan == null ||
                      plansInfo.teams.yearlyPlan == null
                    }
                    onClick={handleTeamsClicked}>
                    Start a trial
                  </Button>
                </div>
              </div>
              <div className="pricing-panel enterprise">
                <div className="header">
                  <div className="panel-title">Enterprise</div>
                  <div className="panel-subtitle">
                    Security, compliance, and flexible deployment
                  </div>
                </div>
                <div className="panel-content">
                  <div className="plan-feature">Unlimited private projects</div>
                  <div className="plan-feature">Single sign-on enabled</div>
                  <div className="plan-feature">Unlimited data storage</div>
                  <div className="plan-feature">Unlimited data retention</div>
                  <div className="plan-feature">Dedicated customer success</div>
                  <div className="plan-feature">Support &amp; Service SLAs</div>
                  <div className="plan-feature">
                    Professional services available
                  </div>
                  <div className="plan-feature">On-Prem available</div>
                  <div className="plan-feature">
                    Hyperparameter optimization tools
                  </div>
                </div>
                <div className="footer">
                  <Button
                    className="action-button"
                    onClick={() => {
                      window.analytics.track('Enterprise contact us clicked', {
                        viewer,
                      });
                      window.open('/site/demo');
                    }}>
                    Contact us
                  </Button>
                </div>
              </div>
            </div>
            <div className="other-plans">
              <a href="/site/academic">
                Academic and open source teams
                <LegacyWBIcon
                  name="launch"
                  className="link-icon"></LegacyWBIcon>
              </a>
            </div>
            <CheckoutModal
              planInfo={selectedPlanInfo}
              artifactPlanInfo={plansInfo.artifact}
              custoEmail={viewer.email}
              open={checkoutModalOpen}
              onTransactionCompleted={() => {
                subscriptionInfo.refetch();
              }}
              onClose={() => {
                setCheckoutModalOpen(false);
              }}
            />
            <StorageCheckoutModal
              planInfo={plansInfo.artifact}
              storageUsed={userEntity.data?.entity?.storageBytes}
              trackingUsed={userEntity.data?.entity?.referenceBytes}
              org={selectedOrganization}
              custoEmail={viewer.email}
              open={storageCheckoutModalOpen}
              onTransactionCompleted={() => {
                subscriptionInfo.refetch();
              }}
              onClose={() => {
                setStorageCheckoutModalOpen(false);
              }}
            />
            <StoragePricingModal
              planInfo={plansInfo.artifact}
              storageUsed={userEntity.data?.entity?.storageBytes}
              trackingUsed={userEntity.data?.entity?.referenceBytes}
              open={storagePricingModalOpen}
              custoEmail={''}
              org={null}
              onTransactionCompleted={() => {}}
              onClose={() => {
                setStoragePricingModalOpen(false);
              }}
            />
          </div>
        </div>
      </Elements>
    );
  },
  {id: 'BillingPage'}
);

interface PaymentModalProps {
  custoEmail: string;
  open: boolean;
  onTransactionCompleted(): void;
  onClose(): void;
}

interface CheckoutModalProps extends PaymentModalProps {
  planInfo: PlanInfo | null;
  artifactPlanInfo: ArtifactPlanInfo | null;
}

const CheckoutModal: FunctionComponent<CheckoutModalProps> = makeComp(
  props => {
    const {planInfo} = props;

    const [showOnboarding, setShowOnboarding] = useState(false);

    if (planInfo == null) {
      return <></>;
    }

    return (
      <Modal
        open={props.open}
        basic
        className="checkout-modal"
        onClose={() => {
          setShowOnboarding(false);
          props.onClose();
        }}>
        {showOnboarding ? (
          <SubscriptionOnboardingForm {...props} />
        ) : (
          <CheckoutModalForm
            {...props}
            onTransactionCompleted={success => {
              if (success) {
                setShowOnboarding(true);
              }
              props.onTransactionCompleted();
            }}
          />
        )}
      </Modal>
    );
  },
  {id: 'CheckoutModal'}
);

type CheckoutModalFormProps = Omit<
  CheckoutModalProps,
  'onTransactionCompleted'
> & {
  onTransactionCompleted(success: boolean): void;
};

const CheckoutModalForm: FunctionComponent<CheckoutModalFormProps> = makeComp(
  props => {
    const {planInfo, artifactPlanInfo} = props;

    // Ensure the plans have prices and intervals. This shouldn't ever happen, but
    // there are some non-stripe plans on the backend that don't have these properties.
    const monthlyPlan = useMemo(() => {
      if (planInfo?.monthlyPlan == null) {
        return null;
      }
      return {
        ...planInfo.monthlyPlan,
        unitPrice: planInfo.monthlyPlan?.unitPrice ?? 0,
        billingInterval: planInfo.monthlyPlan?.billingInterval ?? 'yearly',
      };
    }, [planInfo]);

    const yearlyPlan = useMemo(() => {
      if (planInfo?.yearlyPlan == null) {
        return null;
      }
      return {
        ...planInfo.yearlyPlan,
        unitPrice: planInfo.yearlyPlan?.unitPrice ?? 0,
        billingInterval: planInfo.yearlyPlan?.billingInterval ?? 'yearly',
      };
    }, [planInfo]);

    const storagePlanInfo = useMemo(() => {
      if (artifactPlanInfo?.storagePlan == null) {
        return null;
      }

      return {
        ...artifactPlanInfo?.storagePlan,
        billingInterval: 'N/A',
      };
    }, [artifactPlanInfo]);

    const trackingPlanInfo = useMemo(() => {
      if (artifactPlanInfo?.trackingPlan == null) {
        return null;
      }

      return {
        ...artifactPlanInfo?.trackingPlan,
        billingInterval: 'N/A',
      };
    }, [artifactPlanInfo]);

    if (planInfo == null || artifactPlanInfo == null) {
      return <></>;
    }

    return (
      <div className="checkout pricing-panel">
        <div className="header">
          <div className="panel-title">{planInfo.title}</div>
          <div className="panel-subtitle">{planInfo.subtitle}</div>
        </div>
        <div className="checkout-summary">
          {monthlyPlan &&
          yearlyPlan && ( // Mostly here to help remove the null. In practice this won't display without a plan anyway.
              <SubscribeForm
                custoEmail={props.custoEmail}
                monthlyPlan={monthlyPlan}
                yearlyPlan={yearlyPlan}
                storageMonthly={storagePlanInfo}
                trackingMonthly={trackingPlanInfo}
                pricingPlans={pricingPlans}
                onSubscriptionCompleted={success => {
                  props.onTransactionCompleted(success);
                }}></SubscribeForm>
            )}
        </div>
      </div>
    );
  },
  {id: 'CheckoutModalForm'}
);

interface StorageCheckoutModalProps extends PaymentModalProps {
  planInfo: ArtifactPlanInfo | null;
  storageUsed: number;
  trackingUsed: number;
  org: OrganizationFragment | null;
  // org?: OrganizationFragment;
}

const StorageCheckoutModal: FunctionComponent<StorageCheckoutModalProps> = makeComp(
  props => {
    const {planInfo} = props;

    const [showStorageSuccess, setShowStorageSuccess] = useState(false);

    if (planInfo == null) {
      return <></>;
    }

    return (
      <Modal
        open={props.open}
        basic
        className="checkout-modal"
        onClose={() => {
          setShowStorageSuccess(false);
          props.onClose();
        }}>
        {showStorageSuccess ? (
          <StorageSubscriptionSuccessForm
            {...props}
            onClose={() => {
              setShowStorageSuccess(false);
              props.onClose();
            }}
          />
        ) : (
          <StorageCheckoutModalForm
            {...props}
            onTransactionCompleted={success => {
              if (success) {
                setShowStorageSuccess(true);
              }
              props.onTransactionCompleted();
            }}
          />
        )}
      </Modal>
    );
  },
  {id: 'StorageCheckoutModal'}
);

type StorageCheckoutModalFormProps = Omit<
  StorageCheckoutModalProps,
  'onTransactionCompleted'
> & {
  onTransactionCompleted(success: boolean): void;
};

const StorageCheckoutModalForm: FunctionComponent<StorageCheckoutModalFormProps> = makeComp(
  props => {
    const {planInfo, storageUsed, trackingUsed, org} = props;

    const storagePlanInfo = useMemo(() => {
      if (planInfo?.storagePlan == null) {
        return null;
      }

      return {
        ...planInfo?.storagePlan,
        billingInterval: planInfo.storagePlan?.billingInterval ?? 'N/A',
      };
    }, [planInfo]);

    const trackingPlanInfo = useMemo(() => {
      if (planInfo?.trackingPlan == null) {
        return null;
      }

      return {
        ...planInfo?.trackingPlan,
        billingInterval: planInfo.storagePlan?.billingInterval ?? 'N/A',
      };
    }, [planInfo]);

    const storagePricing = new Map([
      [storageTiers[2], storagePrices[3]],
      [storageTiers[1], storagePrices[2]],
      [storageTiers[0], storagePrices[1]],
    ]);
    const artifactPricing = new Map([
      [storageTiers[2], artifactPrices[3]],
      [storageTiers[1], artifactPrices[2]],
      [storageTiers[0], artifactPrices[1]],
    ]);
    const estimatedBill = (storageUsage: number, artifactUsage: number) => {
      let total = 0.0;
      const calculateTotal = (pricing: Map<number, number>, usage: number) => {
        pricing.forEach((value: number, key: number) => {
          if (usage > key) {
            total += ((usage - key) / bytesInGB) * value;
            usage = key;
          }
        });
      };

      calculateTotal(storagePricing, storageUsage);
      calculateTotal(artifactPricing, artifactUsage);

      return total;
    };

    if (planInfo == null || storagePlanInfo == null) {
      return <></>;
    }

    return (
      <div className="checkout pricing-panel" style={{width: '400px'}}>
        <S.UsageSubscriptionPopup>
          <S.UsageSubscriptionPopupTitle>
            {planInfo.title}
          </S.UsageSubscriptionPopupTitle>
          <S.UsageSubscriptionPopupContent>
            {planInfo.subtitle}
          </S.UsageSubscriptionPopupContent>
          <S.PricingTable>
            <S.PricingTableRow>
              <S.PricingTableHeader>{storageFeatureTitle}</S.PricingTableHeader>
              <S.PricingTableHeader>
                {artifactFeatureTitle}
              </S.PricingTableHeader>
            </S.PricingTableRow>
            <S.PricingTableRow>
              <S.PricingTableBody>
                {storageFeatures.map((tf, i) => (
                  <S.PricingTableFeatureRow>{tf}</S.PricingTableFeatureRow>
                ))}
              </S.PricingTableBody>
              <S.PricingTableBody>
                {artifactFeatures.map((tf, i) => (
                  <S.PricingTableFeatureRow>{tf}</S.PricingTableFeatureRow>
                ))}
              </S.PricingTableBody>
            </S.PricingTableRow>
          </S.PricingTable>
          <S.UsageSubscriptionPopupContent>
            You are currently using {numeral(storageUsed).format('0.0b')} of
            storage and {numeral(trackingUsed).format('0.0b')} of artifacts, so
            your bill would be about{' '}
            {estimatedBill(storageUsed, trackingUsed).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            })}
            .
          </S.UsageSubscriptionPopupContent>
          <div className="checkout-summary" style={{minHeight: '0px'}}>
            {storagePlanInfo && (
              <StorageSubscribeForm
                custoEmail={props.custoEmail}
                storageMonthly={storagePlanInfo}
                trackingMonthly={trackingPlanInfo}
                orgID={org?.id}
                onSubscriptionCompleted={success => {
                  props.onTransactionCompleted(success);
                }}></StorageSubscribeForm>
            )}
          </div>
        </S.UsageSubscriptionPopup>
      </div>
    );
  },
  {id: 'StorageCheckoutModalForm'}
);

const StoragePricingModal: FunctionComponent<StorageCheckoutModalProps> = makeComp(
  props => {
    const {planInfo} = props;

    if (planInfo == null) {
      return <></>;
    }

    return (
      <Modal
        open={props.open}
        basic
        className="checkout-modal"
        onClose={() => {
          props.onClose();
        }}>
        <div className="checkout pricing-panel" style={{width: '406px'}}>
          <S.UsageSubscriptionPopup>
            <S.UsageSubscriptionPopupTitle>
              {planInfo.title}
            </S.UsageSubscriptionPopupTitle>
            <S.UsageSubscriptionPopupContent>
              Your subscription lets you only pay for the storage and artifacts
              that you use each month.
            </S.UsageSubscriptionPopupContent>
            <S.PricingTable>
              <S.PricingTableRow>
                <S.PricingTableHeader>
                  {storageFeatureTitle}
                </S.PricingTableHeader>
                <S.PricingTableHeader>
                  {artifactFeatureTitle}
                </S.PricingTableHeader>
              </S.PricingTableRow>

              <S.PricingTableRow>
                <S.PricingTableBody>
                  {storageFeatures.map((tf, i) => (
                    <S.PricingTableFeatureRow>{tf}</S.PricingTableFeatureRow>
                  ))}
                </S.PricingTableBody>
                <S.PricingTableBody>
                  {artifactFeatures.map((tf, i) => (
                    <S.PricingTableFeatureRow>{tf}</S.PricingTableFeatureRow>
                  ))}
                </S.PricingTableBody>
              </S.PricingTableRow>
            </S.PricingTable>
            <S.UsageSubscriptionPopupContent>
              Need a bespoke plan? Contact us to discuss custom pricing.
            </S.UsageSubscriptionPopupContent>
            <S.UsageSubscriptionPopupContent>
              <Button
                className="action-button"
                onClick={() => {
                  window.analytics.track('Storage plan contact us clicked');
                  window.open('http://wandb.com/demo');
                }}>
                Contact us
              </Button>
            </S.UsageSubscriptionPopupContent>
          </S.UsageSubscriptionPopup>
        </div>
      </Modal>
    );
  },
  {id: 'StorageCheckoutModal'}
);

const SubscriptionOnboardingForm: FunctionComponent = makeComp(
  () => {
    return (
      <div className="checkout pricing-panel confirmation">
        <div className="header">
          <div className="panel-title">Thank you for subscribing!</div>
        </div>
        <div className="checkout-content">
          <div className="checkout-info">
            Learn more about teams in the <a href={docUrl.root}>docs</a>, or
            reach out to us at support@wandb.com for help.
          </div>
          <div className="checkout-info">
            You can now create private teams. Go to your settings page to get
            started.
          </div>
          <Button
            className="onboarding-button"
            onClick={() => {
              window.open('/settings?focusNewTeam');
            }}>
            Settings page →
          </Button>
        </div>
      </div>
    );
  },
  {id: 'SubscriptionOnboardingForm'}
);

type StorageSubscriptionSuccessFormProps = Omit<
  StorageCheckoutModalProps,
  'onClose'
> & {
  onClose(): void; // (success: boolean): void;
};

const StorageSubscriptionSuccessForm: FunctionComponent<StorageSubscriptionSuccessFormProps> = makeComp(
  props => {
    return (
      <div className="checkout pricing-panel" style={{width: '406px'}}>
        <S.UsageSubscriptionPopup>
          <S.UsageSubscriptionPopupTitle>
            Additional space unlocked
          </S.UsageSubscriptionPopupTitle>
          <S.UsageSubscriptionPopupContent>
            Thanks for subscribing to a storage and artifacts plan to unlock
            additional space for your ML projects. Feel free to contact us with
            questions and feedback.
          </S.UsageSubscriptionPopupContent>
          <S.ConfirmationButtons>
            <Button
              className="contact-button"
              width="unset"
              onClick={() => {
                window.analytics.track('Storage plan contact us clicked');
                window.open('http://wandb.com/demo');
              }}>
              Contact us
            </Button>
            <Button
              className="action-button"
              width="unset"
              onClick={() => {
                window.analytics.track('Storage confirmation OK clicked');
                props.onClose();
              }}>
              OK
            </Button>
          </S.ConfirmationButtons>
        </S.UsageSubscriptionPopup>
      </div>
    );
  },
  {id: 'SubscriptionOnboardingForm'}
);

interface UpdatePaymentModalProps extends PaymentModalProps {
  organization: OrganizationFragment | null;
}

const UpdatePaymentModal: FunctionComponent<UpdatePaymentModalProps> = makeComp(
  props => {
    const {organization} = props;

    if (organization == null) {
      return <></>;
    }

    return (
      <Modal
        open={props.open}
        basic
        onClose={props.onClose}
        className="checkout-modal">
        <div className="update-payment pricing-panel">
          <div className="header">
            <div className="panel-title">Update payment method</div>
          </div>
          <div className="checkout-content">
            <UpdatePaymentForm
              custoEmail={props.custoEmail}
              organizationId={organization.id}
              onUpdateCompleted={() => props.onTransactionCompleted()}
            />
          </div>
        </div>
      </Modal>
    );
  },
  {id: 'UpdatePaymentModal'}
);

interface EditSubscriptionModalProps {
  open: boolean;
  organization: OrganizationFragment | null;
  onPlanEdited(): void;
  onClose(): void;
}

const EditSubscriptionModal: FunctionComponent<EditSubscriptionModalProps> = makeComp(
  props => {
    const {open, organization, onPlanEdited, onClose} = props;
    if (organization == null) {
      return <></>;
    }
    return (
      <Modal open={open} basic onClose={onClose} className="checkout-modal">
        <div className="update-payment pricing-panel">
          <div className="header">
            <div className="panel-title">Change plan</div>
          </div>
          <div className="checkout-content">
            <EditSubscriptionForm
              organizationId={organization.id}
              isTrial={organization.subscriptionStatus === 'trialing'}
              isCanceled={
                organization.cancelAtPeriodEnd ||
                organization.subscriptionStatus === 'canceled' ||
                organization.subscriptionStatus === 'incomplete_expired'
              }
              onPlanEdited={onPlanEdited}
            />
          </div>
        </div>
      </Modal>
    );
  },
  {id: 'EditSubscriptionModal'}
);

export default BillingPage;
